// Slice B 主体：assistant 消息的 markdown 渲染。
//
// Round 3 共识：react-markdown@9 + remark-gfm + rehype-sanitize
//   - GFM 表格 / 任务列表 / 删除线 / 自动链接
//   - rehype-sanitize 禁 raw HTML、img、iframe（防 XSS）
//
// Round 5 修订:
//   M-1 流式渲染：用 rAF 节流避免每 delta re-parse；用 sealOpenFences 临时补未闭合 ```
//                让流式中途的 code block 不闪烁
//   V2-8 ErrorBoundary 兜底（外层）
//   V2-10 > 100KB 单条 message 切纯文本模式（移动端性能上限）

import { useEffect, useRef, useState } from "preact/hooks";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { CodeBlock } from "./CodeBlock";
import { MarkdownErrorBoundary } from "./MarkdownErrorBoundary";

// V2-10: 单条 message 超过 100KB 切纯文本模式（Gemini 实测 200KB iPhone 13 已发烫）
const PLAIN_TEXT_THRESHOLD = 100_000;

// rehype-sanitize 配置：
//   - 沿用默认 schema
//   - 额外禁 img / iframe（claude 输出基本不产 img，多余）
//   - 允许 code / pre 上的 className（react-markdown 用它传 language-X）
const sanitizeSchema = {
  ...defaultSchema,
  tagNames: (defaultSchema.tagNames || []).filter(
    (t) => t !== "img" && t !== "iframe"
  ),
  attributes: {
    ...defaultSchema.attributes,
    code: [...((defaultSchema.attributes as any)?.code || []), "className"],
    pre: [...((defaultSchema.attributes as any)?.pre || []), "className"],
    a: [
      ...((defaultSchema.attributes as any)?.a || []),
      ["target", "_blank"],
      ["rel", "noopener", "noreferrer"],
    ],
  },
};

/**
 * M-1: 流式输出中 ``` 经常断在 delta 中间。如果 fence 数量是奇数，
 * 临时补一个 ``` 让 react-markdown 把它当 code block 渲染。
 * 下一帧补全的 delta 进来后正常 parse。
 * Round 3 Opus 提的方案，比 marked 的"不处理 / 接受闪烁"体验好得多。
 */
function sealOpenFences(text: string, streaming: boolean): string {
  if (!streaming) return text;
  const fenceCount = (text.match(/```/g) || []).length;
  return fenceCount % 2 === 1 ? text + "\n```" : text;
}

interface Props {
  text: string;
  streaming: boolean;
}

export function Markdown({ text, streaming }: Props) {
  // M-1: rAF 节流。把"短时间内连续到达的 delta"合并到下一帧渲染。
  // 不用 debounce（debounce 会让流式停顿），用 rAF 天然合并同帧写入。
  const [visible, setVisible] = useState(() => sealOpenFences(text, streaming));
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      setVisible(sealOpenFences(text, streaming));
    });
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [text, streaming]);

  // V2-10: 100KB 降级
  if (text.length > PLAIN_TEXT_THRESHOLD) {
    return (
      <div class="md-oversize">
        <div class="md-oversize__hint">
          ⚠️ 内容过大（{(text.length / 1024).toFixed(0)} KB），已禁用 markdown 渲染
        </div>
        <pre class="md-oversize__pre">{text}</pre>
      </div>
    );
  }

  return (
    <MarkdownErrorBoundary text={text}>
      <div class="md-body">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[[rehypeSanitize, sanitizeSchema]]}
          components={{
            code({ inline, className, children }: any) {
              const txt = String(children).replace(/\n$/, "");
              if (inline) {
                return <code class="inline-code">{txt}</code>;
              }
              const m = /language-(\w+)/.exec(className || "");
              const lang = m?.[1] || "";
              return <CodeBlock lang={lang} code={txt} />;
            },
            // GFM 表格用 wrapper 包一层做横向滚动（移动端窄屏防溢出）
            table({ children }) {
              return (
                <div class="md-table-wrap">
                  <table>{children}</table>
                </div>
              );
            },
            // 链接强制 target=_blank + noopener
            a({ href, children }) {
              return (
                <a href={href} target="_blank" rel="noopener noreferrer">
                  {children}
                </a>
              );
            },
          }}
        >
          {visible}
        </ReactMarkdown>
      </div>
    </MarkdownErrorBoundary>
  );
}
